import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import AxiosClient from '@/api/client';
import { Endpoints } from '@/config/endpoints';
import { API_BASE_URL } from '@/config/constants';
import { type ListPaginated } from '@/types/api';

// ============================================================================
// Types
// ============================================================================

export interface MissedStandard extends Record<string, unknown> {
  id: string;
  text: string;
  department: string;
  touchpoint: string;
  title: string;
  answer: 'no';
  comment: string | null;
  dateAnswered: string; // YYYY-MM-DD format
}

export interface MissedStandardsParams {
  page?: number;
  page_size?: number;
  date?: string; // YYYY-MM-DD format
  department_id?: number;
}

// ============================================================================
// Query Keys Factory
// ============================================================================

export const missedStandardsQueryKeys = {
  all: ['missedStandards'] as const,
  lists: () => [...missedStandardsQueryKeys.all, 'list'] as const,
  list: (assessmentId: string, params?: MissedStandardsParams) =>
    [...missedStandardsQueryKeys.lists(), assessmentId, params] as const,
} as const;

// ============================================================================
// Missed Standards List Query
// ============================================================================

/**
 * Fetches paginated list of missed standards (answer = 'no') for an assessment
 * @param assessmentId - Assessment ID (e.g., ASS_XERX0OY1O42A6VG7)
 * @param params - Query parameters (page, page_size, date, department_id)
 */
export function useMissedStandardsQuery(
  assessmentId: string | undefined,
  params?: MissedStandardsParams,
  options?: Omit<
    UseQueryOptions<ListPaginated<MissedStandard>>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: missedStandardsQueryKeys.list(assessmentId!, params),
    queryFn: async () => {
      if (!assessmentId) throw new Error('Assessment ID is required');

      const endpoint = `${Endpoints.ASSESSMENTS_LIST}/${assessmentId}/missed-standards`;
      const client = new AxiosClient<never, MissedStandard>({
        endpoint,
      });

      const response = await client.getPaginated({ params });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch missed standards');
      }

      return response;
    },
    enabled: !!assessmentId,
    ...options,
  });
}

// ============================================================================
// PDF Export Helper
// ============================================================================

/**
 * Exports missed standards as PDF
 * @param assessmentId - Assessment ID
 * @param filters - Optional filters (date, department_id)
 */
export async function exportMissedStandardsPDF(
  assessmentId: string,
  filters?: { date?: string; department_id?: number }
): Promise<void> {
  await exportMissedStandards(assessmentId, filters, 'pdf');
}

/**
 * Exports missed standards as Excel
 * @param assessmentId - Assessment ID
 * @param filters - Optional filters (date, department_id)
 */
export async function exportMissedStandardsExcel(
  assessmentId: string,
  filters?: { date?: string; department_id?: number }
): Promise<void> {
  await exportMissedStandards(assessmentId, filters, 'excel');
}

/**
 * Generic export function for missed standards
 */
async function exportMissedStandards(
  assessmentId: string,
  filters?: { date?: string; department_id?: number },
  format: 'pdf' | 'excel' = 'pdf'
): Promise<void> {
  try {
    // Build query parameters
    const params = new URLSearchParams();
    if (filters?.date) params.append('date', filters.date);
    if (filters?.department_id)
      params.append('department_id', filters.department_id.toString());

    const queryString = params.toString();
    const exportEndpoint = format === 'excel' ? 'export-excel' : 'export-pdf';
    const endpoint = `${Endpoints.ASSESSMENTS_LIST}/${assessmentId}/missed-standards/${exportEndpoint}${queryString ? `?${queryString}` : ''}`;

    // Create AxiosClient with token support
    const client = new AxiosClient({
      endpoint: endpoint,
      contentType: 'application/json',
      isToken: true, // Token will be automatically added by AxiosClient
      baseURL: API_BASE_URL,
    });

    // Make request using AxiosClient instance with the endpoint
    const response = await client.instance.get(endpoint, {
      responseType: 'blob', // Important for file download
      headers: {
        Accept:
          format === 'excel'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/pdf',
      },
    });

    // Download file
    const blob = new Blob([response.data], {
      type:
        format === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    const extension = format === 'excel' ? 'xlsx' : 'pdf';
    a.download = `missed-standards-${assessmentId}-${new Date().toISOString().split('T')[0]}.${extension}`;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  } catch (error) {
    console.error(`[Export] ${format.toUpperCase()} export failed:`, error);
    throw error;
  }
}
